import { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, Eye, EyeOff, User, Lock, ArrowRight, Shield, Store, ArrowLeft, Building2, Newspaper } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { authApi } from '../../lib/api';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_event-platform-39/artifacts/no11df8z_Blanco-Icono.png";
const BG_IMAGE = "https://images.unsplash.com/photo-1673512703111-c38c42a1f1a3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBuaWdodGNsdWIlMjBpbnRlcmlvciUyMGRhcmslMjBhZXN0aGV0aWN8ZW58MHx8fHwxNzcwMTUxODgxfDA&ixlib=rb-4.1.0&q=85";

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Initialize loginMode from URL query parameter
  const initialMode = (() => {
    const mode = searchParams.get('mode');
    if (mode && ['admin', 'partner', 'producer', 'press'].includes(mode)) {
      return mode;
    }
    return null;
  })();
  
  const [loginMode, setLoginMode] = useState(initialMode);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAdminSubmit = async (e) => {
    e.preventDefault();
    if (!formData.username || !formData.password) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    setLoading(true);
    try {
      const response = await authApi.login(formData.username, formData.password);
      const { access_token, user } = response.data;
      login(access_token, user);
      toast.success(`¡Bienvenido, ${user.full_name}!`);
      navigate('/admin');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Usuario o contraseña incorrectos');
    } finally {
      setLoading(false);
    }
  };

  const handlePartnerSubmit = async (e) => {
    e.preventDefault();
    if (!formData.username || !formData.password) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/partners/login', {
        username: formData.username,
        password: formData.password
      });
      
      if (response.data.success) {
        localStorage.setItem('partner_token', response.data.token);
        localStorage.setItem('partner_data', JSON.stringify(response.data.partner));
        toast.success(`¡Bienvenido, ${response.data.partner.business_name}!`);
        navigate('/partner/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Usuario o contraseña incorrectos');
    } finally {
      setLoading(false);
    }
  };

  const handleProducerSubmit = async (e) => {
    e.preventDefault();
    if (!formData.username || !formData.password) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/producers/login', {
        email: formData.username,
        password: formData.password
      });
      
      if (response.data.access_token) {
        localStorage.setItem('producer_token', response.data.access_token);
        localStorage.setItem('producer_data', JSON.stringify(response.data.producer));
        toast.success(`¡Bienvenido, ${response.data.producer.business_name}!`);
        navigate('/producer/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Email o contraseña incorrectos');
    } finally {
      setLoading(false);
    }
  };

  const handlePressSubmit = async (e) => {
    e.preventDefault();
    if (!formData.username || !formData.password) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/press/login', {
        email: formData.username,
        password: formData.password
      });
      
      if (response.data.access_token) {
        localStorage.setItem('press_token', response.data.access_token);
        localStorage.setItem('press_data', JSON.stringify(response.data.press));
        toast.success(`¡Bienvenido, ${response.data.press.media_name}!`);
        navigate('/prensa/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Email o contraseña incorrectos');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ username: '', password: '' });
    setShowPassword(false);
  };

  const goBack = () => {
    resetForm();
    setLoginMode(null);
  };

  // Selection Screen
  if (loginMode === null) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] relative overflow-hidden">
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: `url(${BG_IMAGE})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/80 to-black" />

        {/* Content */}
        <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="w-full max-w-2xl"
          >
            {/* Logo & Title */}
            <motion.div 
              className="text-center mb-12"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Link to="/" className="inline-block mb-6">
                <motion.img 
                  src={LOGO_URL} 
                  alt="Bravo Producciones" 
                  className="h-20 w-auto mx-auto"
                  whileHover={{ scale: 1.05 }}
                  transition={{ type: "spring", stiffness: 300 }}
                />
              </Link>
              <h1 className="text-4xl font-extrabold uppercase tracking-tight text-white mb-3">
                Sistema Interno
              </h1>
              <p className="text-[#A1A1AA] text-lg">Panel de administración de Bravo</p>
            </motion.div>

            {/* Admin Login - Direct Access */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="max-w-md mx-auto"
            >
              <div className="relative group bg-[#121212]/80 backdrop-blur-xl border border-white/10 rounded-2xl p-8 text-center">
                <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl" />
                
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-white/10 border border-white/20 rounded-xl flex items-center justify-center mb-6 mx-auto">
                    <Shield size={32} className="text-white" />
                  </div>
                  <p className="text-[#A1A1AA] text-sm mb-6">
                    Acceso exclusivo para personal autorizado de Bravo Producciones.
                  </p>
                  <Button
                    onClick={() => setLoginMode('admin')}
                    className="w-full bg-white/10 hover:bg-white/20 text-white border border-white/20 py-3"
                    data-testid="login-option-admin"
                  >
                    <Shield size={18} className="mr-2" />
                    Iniciar Sesión
                  </Button>
                </div>
              </div>
            </motion.div>

            {/* Back Link */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="text-center mt-10"
            >
              <Link 
                to="/" 
                className="inline-flex items-center gap-2 text-[#71717A] hover:text-white transition-colors text-sm"
              >
                <ArrowLeft size={16} />
                Volver al inicio
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>
    );
  }

  // Login Form (Admin, Partner, Producer, or Press)
  const isPartner = loginMode === 'partner';
  const isProducer = loginMode === 'producer';
  const isPress = loginMode === 'press';
  const accentColor = isPartner ? '#D4AF37' : isProducer ? '#A855F7' : isPress ? '#3B82F6' : 'white';
  const modeLabel = isPartner ? 'Portal Partners' : isProducer ? 'Portal Productoras' : isPress ? 'Portal Prensa' : 'Sistema Interno';
  const ModeIcon = isPartner ? Store : isProducer ? Building2 : isPress ? Newspaper : Shield;

  return (
    <div className="min-h-screen bg-[#0A0A0A] relative overflow-hidden">
      {/* Background */}
      <div 
        className="absolute inset-0 bg-cover bg-center opacity-15"
        style={{ backgroundImage: `url(${BG_IMAGE})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/90 to-black" />

      {/* Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Header */}
          <motion.div 
            className="text-center mb-10"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Link to="/" className="inline-block mb-6">
              <img src={LOGO_URL} alt="Bravo Producciones" className="h-20 w-auto mx-auto" />
            </Link>
            <div 
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium uppercase tracking-wider"
              style={{ 
                backgroundColor: isPartner ? 'rgba(212, 175, 55, 0.15)' : isProducer ? 'rgba(168, 85, 247, 0.15)' : isPress ? 'rgba(59, 130, 246, 0.15)' : 'rgba(255, 255, 255, 0.1)',
                color: accentColor,
                border: `1px solid ${isPartner ? 'rgba(212, 175, 55, 0.3)' : isProducer ? 'rgba(168, 85, 247, 0.3)' : isPress ? 'rgba(59, 130, 246, 0.3)' : 'rgba(255, 255, 255, 0.2)'}`
              }}
            >
              <ModeIcon size={16} />
              {modeLabel}
            </div>
          </motion.div>

          {/* Form Card */}
          <motion.div 
            className="bg-[#121212]/90 backdrop-blur-xl border border-[#27272A] rounded-2xl p-8 shadow-[0_8px_32px_rgba(0,0,0,0.4)]"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            {/* Back Button */}
            <button 
              onClick={goBack}
              className="flex items-center gap-2 text-[#71717A] hover:text-white text-sm mb-8 transition-colors group"
            >
              <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
              Cambiar acceso
            </button>

            {/* Title */}
            <h2 className="text-3xl font-extrabold uppercase tracking-tight text-white mb-8">
              Iniciar Sesión
            </h2>

            {/* Form */}
            <form onSubmit={isPartner ? handlePartnerSubmit : isProducer ? handleProducerSubmit : isPress ? handlePressSubmit : handleAdminSubmit} className="space-y-6">
              {/* Username/Email Field */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#A1A1AA] block">
                  {(isProducer || isPress) ? 'Email' : 'Usuario'}
                </Label>
                <div className="relative">
                  <User size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#52525B]" />
                  <input
                    name="username"
                    type={(isProducer || isPress) ? 'email' : 'text'}
                    value={formData.username}
                    onChange={handleInputChange}
                    placeholder={(isProducer || isPress) ? 'email@ejemplo.com' : 'tu_usuario'}
                    className="w-full h-14 bg-[#18181B] border border-[#27272A] rounded-xl pl-12 pr-4 text-white text-lg placeholder:text-[#52525B] focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/30 focus:outline-none transition-all"
                    autoComplete={(isProducer || isPress) ? 'email' : 'username'}
                    data-testid="login-username"
                  />
                </div>
              </div>

              {/* Password Field */}
              <div className="space-y-2">
                <Label className="text-sm font-medium text-[#A1A1AA] block">
                  Contraseña
                </Label>
                <div className="relative">
                  <Lock size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#52525B]" />
                  <input
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="••••••••"
                    className="w-full h-14 bg-[#18181B] border border-[#27272A] rounded-xl pl-12 pr-14 text-white text-lg placeholder:text-[#52525B] focus:border-[#D4AF37] focus:ring-2 focus:ring-[#D4AF37]/30 focus:outline-none transition-all"
                    autoComplete="current-password"
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-[#52525B] hover:text-white transition-colors p-1"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={loading}
                className={`w-full h-14 text-lg font-bold uppercase tracking-wider rounded-xl transition-all active:scale-[0.98] ${
                  isPartner 
                    ? 'bg-gradient-to-r from-[#D4AF37] to-[#F59E0B] hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] text-black' 
                    : isProducer
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 hover:shadow-[0_0_30px_rgba(168,85,247,0.4)] text-white'
                    : isPress
                    ? 'bg-gradient-to-r from-blue-500 to-cyan-500 hover:shadow-[0_0_30px_rgba(59,130,246,0.4)] text-white'
                    : 'bg-white hover:bg-white/90 text-black'
                }`}
                data-testid="login-submit"
              >
                {loading ? (
                  <Loader2 size={22} className="animate-spin" />
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    Ingresar
                    <ArrowRight size={20} />
                  </span>
                )}
              </Button>
            </form>

            {/* Footer */}
            <p className="text-center text-[#52525B] text-sm mt-8">
              {isPartner ? (
                <>
                  ¿No tenés cuenta?{' '}
                  <Link to="/quiero-ser-partner" className="text-[#D4AF37] hover:underline font-medium">
                    Solicitá ser partner
                  </Link>
                </>
              ) : isProducer ? (
                <>
                  ¿No tenés cuenta?{' '}
                  <Link to="/productoras/aplicar" className="text-purple-400 hover:underline font-medium">
                    Registrá tu productora
                  </Link>
                </>
              ) : isPress ? (
                <>
                  ¿No tenés cuenta?{' '}
                  <Link to="/prensa/solicitud" className="text-blue-400 hover:underline font-medium">
                    Solicitá acceso
                  </Link>
                </>
              ) : (
                'Acceso exclusivo para personal autorizado'
              )}
            </p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default LoginPage;
